/**
 * CSV utilities for CRR data handling
 */

export interface CrrRateData {
  direction: 'inbound' | 'outbound';
  groupName: string;
  groupDisplayName: string;
  format: 'P' | 'G' | 'E';
  type: 'tdKg' | 'tdItem';
  rate: number;
}

export interface CrrCsvRow {
  do_from: string;
  do_to: string;
  year: string;
  product: string;
  rg: string;
  td_rate_kg: string;
  td_rate_item: string;
}

/**
 * CN61 extra data extracted from CRR CSV upload
 * These are fields that don't belong to CRR but can be used in CN61
 */
export interface UpuCn61ExtraData {
  direction: 'inbound' | 'outbound';
  format: string; // formatX, registeredItems, insuredItems, expressItems, mbagsWeight
  type: 'weight' | 'items';
  rate: number;
}

/**
 * Result of parsing CRR CSV with extra CN61 data
 */
export interface UpuCrrCsvParseResult {
  crrData: CrrRateData[];
  cn61ExtraData: UpuCn61ExtraData[];
}

/**
 * Parse CSV string into CRR rate data with CN61 extra data extraction
 * @param csvString - The CSV string to parse
 * @param currentData - Current CRR data for context
 * @param currentUserPpo - Current user's PPO
 * @param expectedYear - Expected year to validate against
 * @returns Parsed CRR rate data and CN61 extra data
 */
export function parseCrrCsvWithCn61Extra(csvString: string, currentData?: any, currentUserPpo?: string, expectedYear?: number): UpuCrrCsvParseResult {
  const lines = csvString.trim().split('\n');
  if (lines.length < 2) {
    throw new Error('CSV must have at least a header row and one data row');
  }

  // Parse header - support both old format (ppo_from) and new format (do_from)
  const headers = lines[0].split(',').map(header => header.trim().toLowerCase());
  
  // Map old header names to new ones
  const headerMap: { [key: string]: string } = {
    'ppo_from': 'do_from',
    'ppo_to': 'do_to'
  };
  
  const normalizedHeaders = headers.map(h => headerMap[h] || h);
  
  // Validate required headers
  const requiredHeaders = ['do_from', 'do_to', 'year', 'product', 'rg', 'td_rate_kg', 'td_rate_item'];
  const missingHeaders = requiredHeaders.filter(header => !normalizedHeaders.includes(header));
  
  if (missingHeaders.length > 0) {
    throw new Error(`Missing required headers: ${missingHeaders.join(', ')}. Note: ppo_from/ppo_to are also accepted.`);
  }

  const crrData: CrrRateData[] = [];
  const cn61ExtraData: UpuCn61ExtraData[] = [];
  const errors: string[] = [];

  // CRR valid products (Format P, G, E)
  const crrValidProducts = ['P', 'G', 'E', 'FORMAT P', 'FORMAT G', 'FORMAT E'];
  
  // CN61 extra products (not valid for CRR but valid for CN61)
  const cn61ExtraProducts = ['X', 'FORMAT X', 'REGISTERED', 'INSURED', 'EXPRESS', 'M-BAGS', 'MBAGS'];

  function sanitizeNumericCell(cell: string): string {
    if (cell == null) return '';
    const trimmed = cell.trim();
    // Remove leading apostrophe used to force Excel text formatting
    if (trimmed.startsWith("'")) return trimmed.slice(1);
    // Handle Excel formula-style text wrapper: ="2.000"
    if (/^=\".*\"$/.test(trimmed)) {
      return trimmed.substring(2, trimmed.length - 1);
    }
    return trimmed;
  }

  function validateThreeDecimals(cell: string, fieldName: 'td_rate_kg' | 'td_rate_item', rowIdx: number): boolean {
    if (cell === '') return true; // allow empty as zero
    const threeDecimalPattern = /^\d+(\.\d{3})$/;
    if (!threeDecimalPattern.test(cell)) {
      errors.push(`Row ${rowIdx}: ${fieldName} must have exactly 3 decimal places (e.g., 2.000, 0.125). Got: ${cell}`);
      return false;
    }
    return true;
  }

  // Get header indices using normalized headers
  const getHeaderIndex = (headerName: string) => {
    return normalizedHeaders.indexOf(headerName);
  };
  
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue; // Skip empty lines
    
    const values = line.split(',').map(value => value.trim());
    
    if (values.length !== normalizedHeaders.length) {
      errors.push(`Row ${i + 1} has ${values.length} values but expected ${normalizedHeaders.length}`);
      continue; // Skip this row but continue validation
    }

    const row: any = {
      do_from: values[getHeaderIndex('do_from')],
      do_to: values[getHeaderIndex('do_to')],
      year: values[getHeaderIndex('year')],
      product: values[getHeaderIndex('product')],
      rg: values[getHeaderIndex('rg')],
      td_rate_kg: values[getHeaderIndex('td_rate_kg')],
      td_rate_item: values[getHeaderIndex('td_rate_item')]
    };

    let hasRowError = false;

    // Validate required fields - rg is optional for CN61 extra products
    const productUpper = row.product.toUpperCase();
    const isCn61ExtraProduct = cn61ExtraProducts.includes(productUpper);
    
    if (!row.do_from || !row.do_to || !row.year || !row.product) {
      errors.push(`Row ${i + 1}: Missing required fields`);
      hasRowError = true;
    }
    
    // rg is required for CRR products but optional for CN61 extra products
    if (!isCn61ExtraProduct && !row.rg) {
      errors.push(`Row ${i + 1}: Missing required field 'rg' for CRR product`);
      hasRowError = true;
    }

    // Validate year if expectedYear is provided
    if (expectedYear !== undefined) {
      const rowYear = parseInt(row.year, 10);
      if (isNaN(rowYear)) {
        errors.push(`Row ${i + 1}: Invalid year value '${row.year}'. Must be a valid number`);
        hasRowError = true;
      } else if (rowYear !== expectedYear) {
        errors.push(`Row ${i + 1}: Year mismatch. Expected ${expectedYear} but got ${rowYear}`);
        hasRowError = true;
      }
    }

    // Determine if this is a CRR product or CN61 extra product
    const normalizedProduct = productUpper.replace('FORMAT ', '');
    const isCrrProduct = crrValidProducts.includes(productUpper) || ['P', 'G', 'E'].includes(normalizedProduct);
    
    if (!isCrrProduct && !isCn61ExtraProduct) {
      errors.push(`Row ${i + 1}: Invalid product '${row.product}'. Must be 'Format P', 'Format G', 'Format E', 'Format X', 'Registered', 'Insured', 'Express', or 'M-Bags'`);
      hasRowError = true;
    }

    // Sanitize and validate 3-decimal format
    const sanitizedKg = sanitizeNumericCell(row.td_rate_kg);
    const sanitizedItem = sanitizeNumericCell(row.td_rate_item);
    
    const kgValid = validateThreeDecimals(sanitizedKg, 'td_rate_kg', i + 1);
    const itemValid = validateThreeDecimals(sanitizedItem, 'td_rate_item', i + 1);
    
    if (!kgValid || !itemValid) {
      hasRowError = true;
    }

    // Validate and convert rates
    const tdRateKg = sanitizedKg === '' ? 0 : parseFloat(sanitizedKg);
    const tdRateItem = sanitizedItem === '' ? 0 : parseFloat(sanitizedItem);
    
    if (isNaN(tdRateKg) || tdRateKg < 0) {
      errors.push(`Row ${i + 1}: Invalid td_rate_kg value. Must be a non-negative number`);
      hasRowError = true;
    }
    
    if (isNaN(tdRateItem) || tdRateItem < 0) {
      errors.push(`Row ${i + 1}: Invalid td_rate_item value. Must be a non-negative number`);
      hasRowError = true;
    }

    // Skip adding data if this row has errors
    if (hasRowError) {
      continue;
    }

    // Determine direction based on do_from and do_to
    const filterPpo = currentData?.ppo || 'UNKNOWN';
    const userPpo = currentUserPpo || filterPpo;
    const direction = row.do_from === filterPpo ? 'inbound' : 'outbound';

    if (isCrrProduct) {
      // Add to CRR data
      const normalizedProductFormat = row.product.toUpperCase().replace('FORMAT ', '');

      crrData.push({
        direction: direction as 'inbound' | 'outbound',
        groupName: row.rg || 'UPU',
        groupDisplayName: row.rg || 'UPU',
        format: normalizedProductFormat as 'P' | 'G' | 'E',
        type: 'tdKg' as 'tdKg' | 'tdItem',
        rate: tdRateKg
      });
      
      crrData.push({
        direction: direction as 'inbound' | 'outbound',
        groupName: row.rg || 'UPU',
        groupDisplayName: row.rg || 'UPU',
        format: normalizedProductFormat as 'P' | 'G' | 'E',
        type: 'tdItem' as 'tdKg' | 'tdItem',
        rate: tdRateItem
      });
    } else if (isCn61ExtraProduct) {
      // Add to CN61 extra data
      // Map product names to CN61 format names
      let cn61Format = '';
      if (productUpper === 'X' || productUpper === 'FORMAT X') {
        cn61Format = 'formatX';
      } else if (productUpper === 'REGISTERED') {
        cn61Format = 'registeredItems';
      } else if (productUpper === 'INSURED') {
        cn61Format = 'insuredItems';
      } else if (productUpper === 'EXPRESS') {
        cn61Format = 'expressItems';
      } else if (productUpper === 'M-BAGS' || productUpper === 'MBAGS') {
        cn61Format = 'mbagsWeight';
      }
      
      // Determine which fields to create based on product type
      const isItemsOnlyProduct = ['REGISTERED', 'INSURED', 'EXPRESS'].includes(productUpper);
      const isWeightOnlyProduct = productUpper === 'M-BAGS' || productUpper === 'MBAGS';
      
      if (isItemsOnlyProduct) {
        // Only items
        cn61ExtraData.push({
          direction: direction as 'inbound' | 'outbound',
          format: cn61Format,
          type: 'items',
          rate: tdRateItem
        });
      } else if (isWeightOnlyProduct) {
        // Only weight
        cn61ExtraData.push({
          direction: direction as 'inbound' | 'outbound',
          format: cn61Format,
          type: 'weight',
          rate: tdRateKg
        });
      } else {
        // Both weight and items (Format X)
        cn61ExtraData.push({
          direction: direction as 'inbound' | 'outbound',
          format: cn61Format,
          type: 'weight',
          rate: tdRateKg
        });
        cn61ExtraData.push({
          direction: direction as 'inbound' | 'outbound',
          format: cn61Format,
          type: 'items',
          rate: tdRateItem
        });
      }
    }
  }

  // If there were any errors during validation, throw them all
  if (errors.length > 0) {
    throw new Error(errors.join('|||')); // Use ||| as delimiter to split later
  }

  return { crrData, cn61ExtraData };
}

/**
 * Parse CSV string into CRR rate data (backward compatible version)
 * @param csvString - The CSV string to parse
 * @param currentData - Current CRR data for context
 * @param currentUserPpo - Current user's PPO
 * @param expectedYear - Expected year to validate against
 * @returns Array of CRR rate data objects
 */
export function parseCrrCsv(csvString: string, currentData?: any, currentUserPpo?: string, expectedYear?: number): CrrRateData[] {
  const result = parseCrrCsvWithCn61Extra(csvString, currentData, currentUserPpo, expectedYear);
  return result.crrData;
}

/**
 * Convert CRR data to CSV format
 * @param data - CRR data object
 * @returns CSV string
 */
export function convertCrrDataToCsv(data: any, currentUserPpo?: string): string {
  if (!data || !data.upuCrrData) {
    return 'do_from,do_to,year,product,rg,td_rate_kg,td_rate_item\n';
  }

  const csvRows = ['do_from,do_to,year,product,rg,td_rate_kg,td_rate_item'];
  
  const filterPpo = data.ppo || 'UNKNOWN'; // The DO being processed (from filter)
  const year = data.year || new Date().getFullYear();
  const userPpo = currentUserPpo || filterPpo; // Current user's DO

  // Format value to exactly 3 decimals for CSV content
  function formatTo3Decimals(value: any): string {
    if (value === null || value === undefined || value === '') return '0.000';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '0.000';
    return num.toFixed(3);
  }

  function formatCsvValueForExport(value: any): string {
    return formatTo3Decimals(value);
  }
  
  // Process inbound data
  if (data.upuCrrData.inboundUpuCrrTdRates) {
    data.upuCrrData.inboundUpuCrrTdRates.forEach((item: any) => {
      const groupName = item.upuRemunerationGroupDto?.name || 'UPU';
      const groupDisplayName = item.upuRemunerationGroupDto?.displayName || 'UPU';
      
      // Process each format
      ['formatP', 'formatG', 'formatE'].forEach(formatKey => {
        const format = item[formatKey];
        if (format) {
          const product = `Format ${formatKey.replace('format', '')}`;
          const tdRateKg = format.tdKg || 0;
          const tdRateItem = format.tdItem || 0;
          
          // For inbound: do_from = filter DO (other PPO), do_to = current user DO (receiving from other)
          const weightValue = formatCsvValueForExport(tdRateKg);
          const itemValue = formatCsvValueForExport(tdRateItem);
          csvRows.push(`${filterPpo},${userPpo},${year},${product},${groupName},${weightValue},${itemValue}`);
        }
      });
    });
  }
  
  // Process outbound data
  if (data.upuCrrData.outboundUpuCrrTdRates) {
    data.upuCrrData.outboundUpuCrrTdRates.forEach((item: any) => {
      const groupName = item.upuRemunerationGroupDto?.name || 'UPU';
      const groupDisplayName = item.upuRemunerationGroupDto?.displayName || 'UPU';
      
      // Process each format
      ['formatP', 'formatG', 'formatE'].forEach(formatKey => {
        const format = item[formatKey];
        if (format) {
          const product = `Format ${formatKey.replace('format', '')}`;
          const tdRateKg = format.tdKg || 0;
          const tdRateItem = format.tdItem || 0;
          
          // For outbound: do_from = current user DO, do_to = filter DO (other PPO) (sending to other)
          const weightValue = formatCsvValueForExport(tdRateKg);
          const itemValue = formatCsvValueForExport(tdRateItem);
          csvRows.push(`${userPpo},${filterPpo},${year},${product},${groupName},${weightValue},${itemValue}`);
        }
      });
    });
  }

  return csvRows.join('\n');
}

/**
 * Apply CSV data to CRR data structure
 * @param csvData - Parsed CSV data
 * @param baseData - Base CRR data structure
 * @returns Updated CRR data
 */
export function applyCsvDataToCrr(csvData: CrrRateData[], baseData: any): any {
  const updatedData = { ...baseData };
  
  // Helper to format rate to exactly 3 decimals
  const formatRate = (rate: number): string => {
    return rate.toFixed(3);
  };
  
  // Helper function to normalize group names for matching (trim and lowercase)
  const normalizeKey = (str: string): string => {
    return (str || '').trim().toLowerCase();
  };
  
  // Build a map of existing remuneration groups from baseData to preserve their IDs
  // Use multiple keys to match groups by name, displayName, or combinations
  const existingGroupsMap = new Map<string, any>();
  
  // Helper to add group to map with multiple key variations
  const addGroupToMap = (group: any) => {
    if (!group) return;
    const name = normalizeKey(group.name || '');
    const displayName = normalizeKey(group.displayName || '');
    
    // Add with various key combinations for flexible matching
    existingGroupsMap.set(`${name}_${displayName}`, group);
    existingGroupsMap.set(`${displayName}_${name}`, group);
    existingGroupsMap.set(name, group);
    existingGroupsMap.set(displayName, group);
    // Also add original case-sensitive keys for exact matches
    if (group.name && group.displayName) {
      existingGroupsMap.set(`${group.name}_${group.displayName}`, group);
      existingGroupsMap.set(`${group.displayName}_${group.name}`, group);
    }
  };
  
  // Collect from inbound data
  if (baseData?.upuCrrData?.inboundUpuCrrTdRates) {
    baseData.upuCrrData.inboundUpuCrrTdRates.forEach((item: any) => {
      addGroupToMap(item.upuRemunerationGroupDto);
    });
  }
  
  // Collect from outbound data
  if (baseData?.upuCrrData?.outboundUpuCrrTdRates) {
    baseData.upuCrrData.outboundUpuCrrTdRates.forEach((item: any) => {
      addGroupToMap(item.upuRemunerationGroupDto);
    });
  }
  
  // Helper function to find existing group by CSV group name
  const findExistingGroup = (csvGroupName: string, csvGroupDisplayName: string): any => {
    const normalizedName = normalizeKey(csvGroupName);
    const normalizedDisplayName = normalizeKey(csvGroupDisplayName);
    
    // Try multiple matching strategies
    return existingGroupsMap.get(`${normalizedName}_${normalizedDisplayName}`) ||
           existingGroupsMap.get(`${normalizedDisplayName}_${normalizedName}`) ||
           existingGroupsMap.get(normalizedName) ||
           existingGroupsMap.get(normalizedDisplayName) ||
           existingGroupsMap.get(`${csvGroupName}_${csvGroupDisplayName}`) ||
           existingGroupsMap.get(`${csvGroupDisplayName}_${csvGroupName}`) ||
           null;
  };
  
  // Initialize structures if they don't exist
  if (!updatedData.upuCrrData) {
    updatedData.upuCrrData = {
      inboundUpuCrrTdRates: [],
      outboundUpuCrrTdRates: []
    };
  }
  
  // Only initialize structures for directions that have data
  const hasInboundData = csvData.some(row => row.direction === 'inbound');
  const hasOutboundData = csvData.some(row => row.direction === 'outbound');
  
  if (hasInboundData && !updatedData.upuCrrData.inboundUpuCrrTdRates) {
    updatedData.upuCrrData.inboundUpuCrrTdRates = [];
  }
  if (hasOutboundData && !updatedData.upuCrrData.outboundUpuCrrTdRates) {
    updatedData.upuCrrData.outboundUpuCrrTdRates = [];
  }
  
  // Group CSV data by direction and group
  const groupedData: any = {
    inbound: {},
    outbound: {}
  };
  
  csvData.forEach(row => {
    const key = `${row.groupName}_${row.groupDisplayName}`;
    if (!groupedData[row.direction][key]) {
      groupedData[row.direction][key] = {
        groupName: row.groupName,
        groupDisplayName: row.groupDisplayName,
        formats: {}
      };
    }
    
    if (!groupedData[row.direction][key].formats[row.format]) {
      groupedData[row.direction][key].formats[row.format] = {};
    }
    
    groupedData[row.direction][key].formats[row.format][row.type] = formatRate(row.rate);
  });
  
  // Convert grouped data to CRR format - only for directions that have data
  Object.keys(groupedData).forEach(direction => {
    const directionKey = direction === 'inbound' ? 'inboundUpuCrrTdRates' : 'outboundUpuCrrTdRates';
    
    // Only update the direction that has CSV data, preserve the other direction
    if (direction === 'inbound' && hasInboundData) {
      updatedData.upuCrrData[directionKey] = [];
    } else if (direction === 'outbound' && hasOutboundData) {
      updatedData.upuCrrData[directionKey] = [];
    }
    
    // Only process if this direction has data
    if ((direction === 'inbound' && hasInboundData) || (direction === 'outbound' && hasOutboundData)) {
      Object.values(groupedData[direction]).forEach((group: any) => {
        // Look up existing group to preserve ID and other properties
        const existingGroup = findExistingGroup(group.groupName, group.groupDisplayName);
        
        const crrItem = {
          upuRemunerationGroupDto: existingGroup ? {
            id: existingGroup.id,
            name: existingGroup.name,
            displayName: existingGroup.displayName,
            operatorId: existingGroup.operatorId || null
          } : {
            id: 0,
            name: group.groupName,
            displayName: group.groupDisplayName,
            operatorId: null
          },
          formatP: null,
          formatG: null,
          formatE: null
        };
        
        // Apply format data
        Object.keys(group.formats).forEach(format => {
          const formatKey = `format${format}` as keyof typeof crrItem;
          const formatData = group.formats[format];
          
          if (formatData.tdKg !== undefined || formatData.tdItem !== undefined) {
            (crrItem as any)[formatKey] = {
              tdKg: formatData.tdKg !== undefined ? formatData.tdKg : null,
              tdItem: formatData.tdItem !== undefined ? formatData.tdItem : null
            };
          }
        });
        
        updatedData.upuCrrData[directionKey].push(crrItem);
      });
    }
  });
  
  return updatedData;
}

/**
 * Download CSV file
 * @param csvContent - CSV string content
 * @param filename - Name of the file to download
 */
export function downloadCrrCsv(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

/**
 * Generate sample CSV data for CRR
 * @param ppo - The PPO code to use
 * @param year - The year
 * @returns Sample CSV string
 */
export function generateCrrSampleCsv(ppo: string, year: string): string {
  const sampleData = [
    'do_from,do_to,year,product,rg,td_rate_kg,td_rate_item',
    `${ppo},${ppo},${year},Format P,Group1,0.500,1.000`,
    `${ppo},${ppo},${year},Format G,Group1,0.300,0.800`,
    `${ppo},${ppo},${year},Format E,Group1,0.200,0.500`,
    `${ppo},${ppo},${year},Format P,Group2,0.400,0.900`,
    `${ppo},${ppo},${year},Format G,Group2,0.250,0.700`,
    `${ppo},${ppo},${year},Format E,Group2,0.150,0.400`,
    `${ppo},OTHER,${year},Format P,Group1,0.450,0.950`,
    `${ppo},OTHER,${year},Format G,Group1,0.280,0.750`,
    `${ppo},OTHER,${year},Format E,Group1,0.180,0.450`,
    `${ppo},OTHER,${year},Format P,Group2,0.380,0.850`,
    `${ppo},OTHER,${year},Format G,Group2,0.220,0.650`,
    `${ppo},OTHER,${year},Format E,Group2,0.120,0.350`
  ];
  
  return sampleData.join('\n');
}

/**
 * Convert CN61 extra data to CN61 input format
 * This is used when passing extra data from CRR page to CN61 page
 * @param cn61ExtraData - CN61 extra data extracted from CRR CSV
 * @returns CN61 input format object
 */
export function convertUpuCn61ExtraDataToInputFormat(cn61ExtraData: UpuCn61ExtraData[]): any {
  const result: any = {
    inboundRates: {},
    outboundRates: {}
  };

  // Format rate to exactly 3 decimals
  const formatRate = (rate: number): string => {
    return rate.toFixed(3);
  };

  cn61ExtraData.forEach(row => {
    const ratesKey = row.direction === 'inbound' ? 'inboundRates' : 'outboundRates';
    const formatKey = row.format;
    
    if (!formatKey) return;

    // Determine the structure based on product type
    const isItemsOnlyProduct = ['registeredItems', 'insuredItems', 'expressItems'].includes(formatKey);
    const isWeightOnlyProduct = formatKey === 'mbagsWeight';

    if (isItemsOnlyProduct) {
      // These have a direct rate structure
      if (!result[ratesKey][formatKey]) {
        result[ratesKey][formatKey] = { rate: null };
      }
      if (row.type === 'items') {
        result[ratesKey][formatKey].rate = formatRate(row.rate);
      }
    } else if (isWeightOnlyProduct) {
      // This has a direct rate structure
      if (!result[ratesKey][formatKey]) {
        result[ratesKey][formatKey] = { rate: null };
      }
      if (row.type === 'weight') {
        result[ratesKey][formatKey].rate = formatRate(row.rate);
      }
    } else {
      // Format X has weight/items structure
      if (!result[ratesKey][formatKey]) {
        result[ratesKey][formatKey] = {};
      }
      if (!result[ratesKey][formatKey][row.type]) {
        result[ratesKey][formatKey][row.type] = { rate: null };
      }
      result[ratesKey][formatKey][row.type].rate = formatRate(row.rate);
    }
  });

  return result;
}
